import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SafeUrl } from "@angular/platform-browser";
import { Subject, forkJoin, of } from "rxjs";
import {
  catchError,
  finalize,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { ClipboardService } from "../clipboard.service";
import { DigitalCardService } from "../digital-card.service";
import {
  SHARE_QR,
  SHARE_QR_PNG_PX,
  SHARE_QR_PREVIEW_PX,
  SHARE_QR_SVG_PX,
} from "../../shared/qr-render";
import { EmployeePickerItem } from "../../employees/employees.interface";
import type {
  DigitalCardProfileItem,
  DigitalCardSourceShareDialogData,
} from "../digital-cards.interface";

type ShareMode = "single" | "bulk";

interface BulkLinkRow {
  profileId: string;
  email: string;
  displayName: string;
  sourceUrl: string;
}

@Component({
  selector: "app-digital-card-source-share-dialog",
  templateUrl: "./digital-card-source-share-dialog.component.html",
  styleUrls: ["./digital-card-source-share-dialog.component.scss"],
  standalone: false,
})
export class DigitalCardSourceShareDialogComponent implements OnDestroy {
  readonly shareQr = SHARE_QR;
  readonly qrSvgPx = SHARE_QR_SVG_PX;
  readonly qrPngPx = SHARE_QR_PNG_PX;
  readonly qrPreviewPx = SHARE_QR_PREVIEW_PX;
  mode: ShareMode = "single";
  previewEmployee: EmployeePickerItem | null = null;
  sourceUrl: string | null = null;
  loading = false;
  ready = false;
  employeePickerReady = false;
  singleContentRevealed = false;
  noPublishedCard = false;
  errorKey: string | null = null;
  qrSvgDownloadLink: SafeUrl | null = null;
  qrPngDownloadLink: SafeUrl | null = null;
  private singleCardName = "";

  private bulkRows: BulkLinkRow[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly singleRequest$ = new Subject<EmployeePickerItem | null>();
  private readonly bulkRequest$ = new Subject<void>();

  @ViewChild("downloadQrSvgRef")
  downloadQrSvgRef?: ElementRef<HTMLAnchorElement>;
  @ViewChild("downloadQrPngRef")
  downloadQrPngRef?: ElementRef<HTMLAnchorElement>;

  constructor(
    public dialogRef: MatDialogRef<DigitalCardSourceShareDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DigitalCardSourceShareDialogData,
    private clipboard: ClipboardService,
    private digitalCardService: DigitalCardService,
  ) {
    this.singleRequest$
      .pipe(
        switchMap((employee) => {
          this.sourceUrl = null;
          this.ready = false;
          this.noPublishedCard = false;
          this.errorKey = null;
          this.qrSvgDownloadLink = null;
          this.qrPngDownloadLink = null;
          this.singleCardName = "";
          const email = (employee?.email ?? "").trim();
          if (!email) return of(null);
          this.loading = true;
          return this.digitalCardService.getProfiles({ forEmail: email }).pipe(
            switchMap((res) => {
              const emailLower = email.toLowerCase();
              const published = (res.profiles ?? []).filter(
                (p) =>
                  (p.email ?? "").trim().toLowerCase() === emailLower &&
                  p.status === "published",
              );
              const profile =
                published.find((p) => p.isDefault) ?? published[0] ?? null;
              if (!profile) return of(null);
              this.singleCardName = profile.displayName?.trim() || "";
              return this.digitalCardService
                .getSourceTokens(profile.profileId, [this.data.medium])
                .pipe(
                  map(
                    (tokens) =>
                      tokens[this.data.medium]?.sourceUrl?.trim() || null,
                  ),
                );
            }),
            catchError(() => {
              if (this.mode === "single") {
                this.errorKey = "digitalCards.sources.shareFailed";
              }
              return of(null);
            }),
            finalize(() => {
              if (this.mode !== "single") return;
              this.loading = false;
              this.ready = true;
              this.singleContentRevealed = true;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((url) => {
        if (this.mode !== "single") return;
        if (!this.previewEmployee) {
          this.noPublishedCard = false;
          this.sourceUrl = null;
          this.ready = false;
          return;
        }
        if (url == null && this.errorKey) return;
        if (url == null) {
          this.noPublishedCard = true;
          this.sourceUrl = null;
          return;
        }
        this.sourceUrl = url;
        this.noPublishedCard = false;
        this.errorKey = null;
      });

    this.bulkRequest$
      .pipe(
        switchMap(() => {
          this.bulkRows = [];
          this.ready = false;
          this.errorKey = null;
          this.loading = true;
          return this.digitalCardService
            .getProfiles({ templatesOnly: true })
            .pipe(
              switchMap((templatesRes) => {
                const templates = (templatesRes.profiles ?? []).filter(
                  (p) => !p.email && p.status === "published",
                );
                if (templates.length === 0) {
                  return of([] as DigitalCardProfileItem[]);
                }
                const ordered = [
                  ...templates.filter((t) => t.isDefault),
                  ...templates.filter((t) => !t.isDefault),
                ];
                return forkJoin(
                  ordered.map((template) =>
                    this.digitalCardService.getProfiles({
                      forTemplate: template.profileId,
                    }),
                  ),
                ).pipe(
                  map((responses) => {
                    const byEmail = new Map<string, DigitalCardProfileItem>();
                    for (const res of responses) {
                      for (const profile of res.profiles ?? []) {
                        const email = (profile.email ?? "")
                          .trim()
                          .toLowerCase();
                        if (!email || profile.status !== "published") continue;
                        if (!byEmail.has(email)) byEmail.set(email, profile);
                      }
                    }
                    return [...byEmail.values()];
                  }),
                );
              }),
              switchMap((profiles) => {
                if (profiles.length === 0) return of([] as BulkLinkRow[]);
                return this.digitalCardService
                  .getSourceTokensBulk(
                    profiles.map((p) => p.profileId),
                    [this.data.medium],
                  )
                  .pipe(
                    map((tokensByProfile) => {
                      const rows: BulkLinkRow[] = [];
                      for (const profile of profiles) {
                        const sourceUrl =
                          tokensByProfile[profile.profileId]?.[
                            this.data.medium
                          ]?.sourceUrl?.trim() || "";
                        if (!sourceUrl) continue;
                        rows.push({
                          profileId: profile.profileId,
                          email: (profile.email ?? "").trim(),
                          displayName: profile.displayName || "",
                          sourceUrl,
                        });
                      }
                      return rows.sort((a, b) =>
                        a.email.localeCompare(b.email),
                      );
                    }),
                  );
              }),
              catchError(() => {
                if (this.mode === "bulk") {
                  this.errorKey = "digitalCards.sources.shareFailed";
                }
                return of([] as BulkLinkRow[]);
              }),
              finalize(() => {
                if (this.mode !== "bulk") return;
                this.loading = false;
                this.ready = true;
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((rows) => {
        if (this.mode !== "bulk") return;
        this.bulkRows = rows;
        if (!this.errorKey && rows.length === 0) {
          this.errorKey = "digitalCards.sources.bulkEmpty";
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get sourceName(): string {
    return this.data.sourceName ?? "";
  }

  get showLoading(): boolean {
    if (this.mode === "bulk") return this.loading;
    if (!this.employeePickerReady) return true;
    if (!this.singleContentRevealed && this.loading) return true;
    return this.loading;
  }

  get showEmployeePicker(): boolean {
    if (this.mode !== "single" || !this.employeePickerReady) return false;
    return this.singleContentRevealed || !this.loading;
  }

  close(): void {
    this.dialogRef.close();
  }

  setMode(mode: ShareMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.errorKey = null;
    this.ready = false;
    this.loading = false;
    this.sourceUrl = null;
    this.bulkRows = [];
    this.qrSvgDownloadLink = null;
    this.qrPngDownloadLink = null;
    this.singleCardName = "";
    if (mode === "single") {
      this.employeePickerReady = false;
      this.singleContentRevealed = false;
      if (this.previewEmployee) {
        this.singleRequest$.next(this.previewEmployee);
      }
      return;
    }
    this.bulkRequest$.next();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    if (!this.previewEmployee) this.singleContentRevealed = true;
  }

  onEmployeeChange(employee: EmployeePickerItem | null): void {
    this.previewEmployee = employee;
    if (this.mode !== "single") return;
    this.singleRequest$.next(employee);
  }

  retry(): void {
    this.errorKey = null;
    if (this.mode === "bulk") {
      this.bulkRequest$.next();
      return;
    }
    if (this.previewEmployee) this.singleRequest$.next(this.previewEmployee);
  }

  get qrFileBase(): string {
    const rawName =
      this.singleCardName ||
      this.previewEmployee?.fullName?.trim() ||
      this.previewEmployee?.givenName?.trim() ||
      "";
    const rawMail = this.previewEmployee?.email?.trim() || "";
    const name = rawName
      .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    const mail = rawMail
      .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    if (name && mail) {
      return `qr-${name}-${mail}`;
    }
    if (name) {
      return `qr-${name}`;
    }
    if (mail) {
      return `qr-${mail}`;
    }
    return "qr";
  }

  copyLink(): void {
    const value = (this.sourceUrl ?? "").trim();
    if (!value) return;
    this.clipboard.copy(value, "digitalCard.owner.linkCopied");
  }

  downloadCsv(): void {
    if (this.bulkRows.length === 0) return;
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [
      ["email", "displayName", "sourceUrl"].join(","),
      ...this.bulkRows.map((row) =>
        [row.email, row.displayName, row.sourceUrl].map(escape).join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign-${this.data.sourceId}-links.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadQrSvg(): void {
    this.downloadQrSvgRef?.nativeElement?.click();
  }

  downloadQrPng(): void {
    this.downloadQrPngRef?.nativeElement?.click();
  }
}
